import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';

/** A single item inside a RowActionsMenu. */
interface RowAction {
  label: string;
  onClick: () => void;
  /** Render in a danger colour (e.g. Block/Revoke). */
  danger?: boolean;
  /** Hide this action entirely. */
  hidden?: boolean;
}

/**
 * Compact per-row "⋯" actions dropdown. Keeps table rows a single line high
 * instead of stacking many inline buttons. Closes on outside click / Escape.
 */
function RowActionsMenu({ actions }: { actions: RowAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const visible = actions.filter((a) => !a.hidden);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (visible.length === 0) return null;

  return (
    <div ref={ref} className="relative flex justify-end">
      <Button
        variant="ghost"
        size="sm"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-[10rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-border-dark dark:bg-surface-dark"
        >
          {visible.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                a.onClick();
              }}
              className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 dark:hover:bg-surface-dark-secondary ${
                a.danger
                  ? 'text-danger-600 dark:text-danger-400'
                  : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  roleId: string;
  jobRole: string | null;
  department: string;
  hourlyRate: number | null;
  licenceType: string;
  status: 'active' | 'inactive' | 'invited' | 'locked';
  lastLoginAt: string | null;
  createdAt: string;
}

interface Role {
  id: string;
  name: string;
}

// Job role options imported from shared constants
import { JOB_ROLE_OPTIONS } from '@/lib/constants';

interface InviteUserData {
  name: string;
  email: string;
  roleId: string;
  jobRole: string;
  department: string;
  hourlyRate: number | null;
  licenceType: string;
}

/**
 * UsersManagement — user directory with invitation and licence tracking.
 * Includes tabs for Staff Users and Portal Users (Clients).
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5, 14.8
 */
export function UsersManagement() {
  const { can } = usePermissions();
  const { activeTab, onChange } = useTabs('staff');

  const tabs = [
    { id: 'staff', label: 'Staff Users' },
    ...(can(PERMISSIONS.CLIENTS_MANAGE_PORTAL) ? [{ id: 'portal', label: 'Portal Users' }] : []),
  ];

  return (
    <div className="space-y-6">
      <Tabs tabs={tabs} activeTab={activeTab} onChange={onChange} />

      <TabPanel tabId="staff" activeTab={activeTab}>
        <StaffUsersTab />
      </TabPanel>

      {can(PERMISSIONS.CLIENTS_MANAGE_PORTAL) && (
        <TabPanel tabId="portal" activeTab={activeTab}>
          <PortalUsersSection />
        </TabPanel>
      )}
    </div>
  );
}

// ─── Staff Users Tab ──────────────────────────────────────────────────────────

function StaffUsersTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  // Debounce the search input so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['settings', 'users', { status: statusFilter, search, page, pageSize, sort }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (sort.direction) {
        params.set('sortBy', sort.column);
        params.set('sortDirection', sort.direction);
      }
      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        meta?: { page: number; pageSize: number; totalCount: number };
      }>(`/users?${params.toString()}`);

      const raw = response.data.data || [];
      const users: User[] = raw.map((u) => ({
        id: u.id as string,
        name: (u.name || '') as string,
        email: (u.email || '') as string,
        role: (u.role_name || u.role || '') as string,
        roleId: (u.role_id || u.roleId || '') as string,
        jobRole: ((u.job_role || u.jobRole || null) as string | null)?.toLowerCase() ?? null,
        department: (u.department || '') as string,
        hourlyRate: (u.hourly_rate ?? u.hourlyRate ?? null) as number | null,
        licenceType: (u.licence_type || u.licenceType || '') as string,
        status: (u.status || 'active') as User['status'],
        lastLoginAt: (u.last_login_at || u.lastLoginAt || null) as string | null,
        createdAt: (u.created_at || u.createdAt || '') as string,
      }));

      const meta = response.data.meta;
      return {
        users,
        total: meta?.totalCount || users.length,
        page: meta?.page || page,
        pageSize: meta?.pageSize || pageSize,
      };
    },
  });

  const { data: licenceInfo } = useQuery({
    queryKey: ['settings', 'licence-info'],
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>>>('/settings/licences');
      const raw = response.data.data;
      return {
        total: (raw.licenceCount ?? raw.total ?? 15) as number,
        used: (raw.activeUserCount ?? raw.used ?? 0) as number,
        available: (raw.availableLicences ?? raw.available ?? 15) as number,
        utilizationPercent: (raw.utilisationPercentage ?? raw.utilizationPercent ?? 0) as number,
      };
    },
  });

  const { data: roles } = useQuery({
    queryKey: ['settings', 'roles-list'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Role[]>>('/roles');
      return response.data.data;
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async (data: InviteUserData) => {
      const response = await apiClient.post<ApiResponse<User>>('/users/invite', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'licence-info'] });
      setIsInviteOpen(false);
      toast({ variant: 'success', title: 'Invitation sent successfully' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/users/${userId}/deactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'licence-info'] });
      setDeactivatingUser(null);
      toast({ variant: 'success', title: 'User deactivated' });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InviteUserData> }) => {
      const response = await apiClient.put<ApiResponse<User>>(`/users/${id}`, data);
      return response.data.data;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
      setEditingUser(null);
      // The backend moves a user to 'invited' only when the login email was
      // changed — surface the deactivation + re-verification outcome.
      const rawStatus = (updated as unknown as { status?: string })?.status;
      if (rawStatus === 'invited') {
        toast({
          variant: 'success',
          title: 'Email changed — user deactivated',
          description:
            'A verification email was sent to the new address. The account reactivates once the user verifies it.',
        });
      } else {
        toast({ variant: 'success', title: 'User updated successfully' });
      }
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      setResendingUserId(userId);
      await apiClient.post(`/users/${userId}/resend-invitation`);
    },
    onSuccess: () => {
      toast({ variant: 'success', title: 'Invitation resent' });
    },
    onSettled: () => {
      setResendingUserId(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/users/${userId}/activate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'licence-info'] });
      toast({ variant: 'success', title: 'User reactivated' });
    },
  });

  const columns: ColumnDef<User>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.name}</span>
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.email}</p>
          </div>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        sortable: true,
        cell: (row) => <span className="text-sm text-gray-700 dark:text-gray-300">{row.role}</span>,
      },
      {
        id: 'department',
        header: 'Department',
        accessorKey: 'department',
        sortable: true,
      },
      {
        id: 'licenceType',
        header: 'Licence',
        cell: (row) => (
          <Badge variant="info" size="sm">
            {row.licenceType || 'Standard'}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row) => {
          const variant =
            row.status === 'active'
              ? 'success'
              : row.status === 'invited'
                ? 'info'
                : row.status === 'locked'
                  ? 'danger'
                  : 'default';
          return (
            <Badge variant={variant} size="sm" dot>
              {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
            </Badge>
          );
        },
      },
      {
        id: 'lastLogin',
        header: 'Last Login',
        sortable: true,
        cell: (row) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleDateString() : 'Never'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditingUser(row)}>
              Edit
            </Button>
            {row.status === 'invited' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => resendInviteMutation.mutate(row.id)}
                loading={resendingUserId === row.id}
                disabled={resendInviteMutation.isPending}
              >
                Resend
              </Button>
            )}
            {row.status === 'active' && (
              <Button variant="ghost" size="sm" onClick={() => setDeactivatingUser(row)}>
                Deactivate
              </Button>
            )}
            {(row.status === 'inactive' || row.status === 'locked') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => reactivateMutation.mutate(row.id)}
                loading={reactivateMutation.isPending}
              >
                Reactivate
              </Button>
            )}
          </div>
        ),
      },
    ],
    [resendInviteMutation],
  );

  const pagination: PaginationState | undefined = usersData
    ? { page: usersData.page, pageSize: usersData.pageSize, total: usersData.total }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);

  return (
    <div className="space-y-6">
      {/* Licence Usage */}
      {licenceInfo && (
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Licence Usage
              </h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {licenceInfo.used} of {licenceInfo.total} licences used (
                {licenceInfo.utilizationPercent}%)
              </p>
            </div>
            <div className="w-48">
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-2 rounded-full transition-all ${
                    licenceInfo.utilizationPercent >= 90
                      ? 'bg-danger-500'
                      : licenceInfo.utilizationPercent >= 75
                        ? 'bg-warning-500'
                        : 'bg-success-500'
                  }`}
                  style={{ width: `${Math.min(licenceInfo.utilizationPercent, 100)}%` }}
                />
              </div>
            </div>
          </div>
          {licenceInfo.utilizationPercent >= 90 && (
            <p className="mt-2 text-xs text-warning-600 dark:text-warning-400">
              ⚠ Approaching licence limit. Contact your administrator to add more licences.
            </p>
          )}
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader
          title="Users"
          description="Manage user accounts, invitations, and access."
          action={
            <Button
              size="sm"
              onClick={() => setIsInviteOpen(true)}
              disabled={licenceInfo ? licenceInfo.available <= 0 : false}
            >
              Invite User
            </Button>
          }
        />
        <CardContent>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="sm:w-72">
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, email, or department..."
                aria-label="Search users"
              />
            </div>
            <div className="sm:w-48">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'invited', label: 'Invited' },
                  { value: 'locked', label: 'Locked' },
                ]}
                placeholder="All statuses"
              />
            </div>
          </div>
          <DataTable<User>
            data={usersData?.users ?? []}
            columns={columns}
            keyExtractor={(row) => row.id}
            sort={sort}
            onSortChange={handleSortChange}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            emptyMessage="No users found."
          />
        </CardContent>
      </Card>

      {/* Invite User Modal */}
      {isInviteOpen && (
        <InviteUserModal
          roles={roles ?? []}
          onInvite={(data) => inviteMutation.mutate(data)}
          onClose={() => setIsInviteOpen(false)}
          isSaving={inviteMutation.isPending}
        />
      )}

      {/* Deactivate Confirmation */}
      {deactivatingUser && (
        <Dialog
          open
          onClose={() => setDeactivatingUser(null)}
          onConfirm={() => deactivateMutation.mutate(deactivatingUser.id)}
          title="Deactivate User"
          description={`Are you sure you want to deactivate ${deactivatingUser.name}? Their active jobs and tasks will be moved to the unassigned queue.`}
          confirmLabel="Deactivate"
          confirmVariant="danger"
          loading={deactivateMutation.isPending}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles ?? []}
          isLastActiveAdmin={
            editingUser.role === 'Administrator' &&
            editingUser.status === 'active' &&
            !(usersData?.users ?? []).some(
              (u) => u.id !== editingUser.id && u.role === 'Administrator' && u.status === 'active',
            )
          }
          onSave={(data) => updateUserMutation.mutate({ id: editingUser.id, data })}
          onClose={() => setEditingUser(null)}
          isSaving={updateUserMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Invite User Modal ────────────────────────────────────────────────────────

interface InviteUserModalProps {
  roles: Role[];
  onInvite: (data: InviteUserData) => void;
  onClose: () => void;
  isSaving: boolean;
}

function InviteUserModal({ roles, onInvite, onClose, isSaving }: InviteUserModalProps) {
  const [form, setForm] = useState<InviteUserData>({
    name: '',
    email: '',
    roleId: '',
    jobRole: '',
    department: '',
    hourlyRate: null,
    licenceType: 'standard',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onInvite(form);
  };

  return (
    <Modal open onClose={onClose} title="Invite New User" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          placeholder="Jane Smith"
        />
        <Input
          label="Email Address"
          type="email"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          required
          placeholder="jane@yourfirm.ca"
          hint="An invitation email will be sent to this address (valid for 72 hours)"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            value={form.roleId}
            onChange={(e) => setForm((prev) => ({ ...prev, roleId: e.target.value }))}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            required
            placeholder="Select a role"
          />
          <Select
            label="Job Role"
            value={form.jobRole}
            onChange={(e) => setForm((prev) => ({ ...prev, jobRole: e.target.value }))}
            options={JOB_ROLE_OPTIONS}
            placeholder="Select job role"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Department"
            value={form.department}
            onChange={(e) => setForm((prev) => ({ ...prev, department: e.target.value }))}
            placeholder="e.g., Tax"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Hourly Rate ($)"
            type="number"
            value={form.hourlyRate != null ? String(form.hourlyRate) : ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                hourlyRate: e.target.value ? Number(e.target.value) : null,
              }))
            }
            min={0}
            step="0.01"
            placeholder="0.00"
          />
          <Select
            label="Licence Type"
            value={form.licenceType}
            onChange={(e) => setForm((prev) => ({ ...prev, licenceType: e.target.value }))}
            options={[
              { value: 'standard', label: 'Standard' },
              { value: 'job', label: 'Job Management' },
              { value: 'billing', label: 'Billing' },
              { value: 'portal', label: 'Portal Access' },
            ]}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={!form.name || !form.email || !form.roleId}
          >
            Send Invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────

interface EditUserModalProps {
  user: User;
  roles: Role[];
  /** True when this user is the tenant's only active Administrator. */
  isLastActiveAdmin: boolean;
  onSave: (data: Partial<InviteUserData>) => void;
  onClose: () => void;
  isSaving: boolean;
}

function EditUserModal({
  user,
  roles,
  isLastActiveAdmin,
  onSave,
  onClose,
  isSaving,
}: EditUserModalProps) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    roleId: user.roleId,
    jobRole: user.jobRole?.toLowerCase() || '',
    department: user.department,
    hourlyRate: user.hourlyRate,
    licenceType: user.licenceType || 'standard',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Changing the email deactivates the user until they verify the new
    // address. Confirm, then surface the outcome after save.
    const emailChanged =
      form.email.trim().toLowerCase() !== (user.email ?? '').trim().toLowerCase();
    if (emailChanged) {
      const confirmed = window.confirm(
        `Changing the email to "${form.email.trim()}" will deactivate this user. ` +
          `They will be signed out and must verify the new address (a verification ` +
          `email will be sent) before the account is reactivated.\n\nContinue?`,
      );
      if (!confirmed) return;
    }

    onSave(form);
  };

  return (
    <Modal open onClose={onClose} title={`Edit User: ${user.name}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            value={form.roleId}
            onChange={(e) => setForm((prev) => ({ ...prev, roleId: e.target.value }))}
            options={roles.map((r) => ({
              value: r.id,
              label: r.name,
              // Last active admin can't be moved off Administrator — only the
              // Administrator option stays selectable. Backend enforces this too.
              disabled: isLastActiveAdmin && r.name !== 'Administrator',
            }))}
            required
            placeholder="Select a role"
            hint={
              isLastActiveAdmin
                ? 'This is the only administrator. Assign another administrator before changing this role.'
                : undefined
            }
          />
          <Select
            label="Job Role"
            value={form.jobRole}
            onChange={(e) => setForm((prev) => ({ ...prev, jobRole: e.target.value }))}
            options={JOB_ROLE_OPTIONS}
            placeholder="Select job role"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Department"
            value={form.department}
            onChange={(e) => setForm((prev) => ({ ...prev, department: e.target.value }))}
            placeholder="e.g., Tax"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Hourly Rate ($)"
            type="number"
            value={form.hourlyRate != null ? String(form.hourlyRate) : ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                hourlyRate: e.target.value ? Number(e.target.value) : null,
              }))
            }
            min={0}
            step="0.01"
            placeholder="0.00"
          />
          <Select
            label="Licence Type"
            value={form.licenceType}
            onChange={(e) => setForm((prev) => ({ ...prev, licenceType: e.target.value }))}
            options={[
              { value: 'standard', label: 'Standard' },
              { value: 'job', label: 'Job Management' },
              { value: 'billing', label: 'Billing' },
              { value: 'portal', label: 'Portal Access' },
            ]}
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          required
          placeholder="jane@yourfirm.ca"
          hint="Changing the email deactivates the user until they verify the new address."
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={!form.name || !form.email || !form.roleId}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Portal Users Section ─────────────────────────────────────────────────────

interface PortalUser {
  id: string;
  contactId: string;
  contactName: string;
  email: string;
  status: 'invited' | 'active' | 'suspended';
  accessBlocked: boolean;
  mfaEnabled: boolean | null;
  lastLoginAt: string | null;
  createdAt: string;
  featureConfig: Record<string, boolean>;
}

function PortalUsersSection() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { can } = usePermissions();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [editingPortalUser, setEditingPortalUser] = useState<PortalUser | null>(null);
  const [resendingPortalUserId, setResendingPortalUserId] = useState<string | null>(null);

  const { data: portalUsersData, isLoading } = useQuery({
    queryKey: ['settings', 'portal-users', { status: statusFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
      }>(`/portal/users?${params.toString()}`);

      const raw = response.data.data || [];
      return raw.map((u) => {
        // Parse feature_config, default all to true
        const defaultFeatures = {
          jobs: true,
          documents: true,
          invoices: true,
          messages: true,
          notifications: true,
        };
        let featureConfig = defaultFeatures;
        try {
          const stored = u.feature_config;
          if (stored) {
            const parsed = typeof stored === 'string' ? JSON.parse(stored as string) : stored;
            featureConfig = { ...defaultFeatures, ...(parsed as Record<string, boolean>) };
          }
        } catch {
          // use defaults
        }

        return {
          id: u.id as string,
          contactId: (u.contact_id || u.contactId || '') as string,
          contactName:
            `${(u.first_name || '') as string} ${(u.last_name || '') as string}`.trim() ||
            'Contact',
          email: (u.email || '') as string,
          status: (u.status || 'invited') as PortalUser['status'],
          accessBlocked: Boolean(u.access_blocked),
          mfaEnabled:
            u.mfa_enabled === null || u.mfa_enabled === undefined ? null : Boolean(u.mfa_enabled),
          lastLoginAt: (u.last_login_at || u.lastLoginAt || null) as string | null,
          createdAt: (u.created_at || u.createdAt || '') as string,
          featureConfig,
        };
      }) as PortalUser[];
    },
  });

  // Client-side search filtering
  const filteredPortalUsers = useMemo(() => {
    const users = portalUsersData ?? [];
    if (!searchFilter.trim()) return users;
    const q = searchFilter.toLowerCase();
    return users.filter(
      (u) => u.contactName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [portalUsersData, searchFilter]);

  const suspendMutation = useMutation({
    mutationFn: async (portalUserId: string) => {
      await apiClient.patch(`/portal/users/${portalUserId}/suspend`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      toast({ variant: 'success', title: 'Portal user suspended' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (portalUserId: string) => {
      await apiClient.patch(`/portal/users/${portalUserId}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      toast({ variant: 'success', title: 'Portal user reactivated' });
    },
  });

  const resendMutation = useMutation({
    mutationFn: async (portalUserId: string) => {
      setResendingPortalUserId(portalUserId);
      await apiClient.post(`/portal/users/${portalUserId}/resend-invitation`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      toast({ variant: 'success', title: 'Invitation resent' });
    },
    onSettled: () => {
      setResendingPortalUserId(null);
    },
  });

  const updatePortalUserMutation = useMutation({
    mutationFn: async ({
      portalUserId,
      email,
      feature_config,
    }: {
      portalUserId: string;
      email?: string;
      feature_config?: Record<string, boolean>;
    }) => {
      await apiClient.patch(`/portal/users/${portalUserId}`, { email, feature_config });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      setEditingPortalUser(null);
      toast({ variant: 'success', title: 'Portal user updated successfully' });
    },
    onError: () => {
      toast({ variant: 'error', title: 'Failed to update portal user' });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (portalUserId: string) => {
      await apiClient.post(`/portal/users/${portalUserId}/revoke-invitation`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      toast({ variant: 'success', title: 'Invitation revoked' });
    },
    onError: () => toast({ variant: 'error', title: 'Failed to revoke invitation' }),
  });

  const accessMutation = useMutation({
    mutationFn: async ({ portalUserId, blocked }: { portalUserId: string; blocked: boolean }) => {
      await apiClient.patch(`/portal/users/${portalUserId}/access`, { blocked });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      toast({
        variant: 'success',
        title: variables.blocked ? 'Portal access blocked' : 'Portal access granted',
      });
    },
    onError: () => toast({ variant: 'error', title: 'Failed to update access' }),
  });

  const mfaMutation = useMutation({
    mutationFn: async ({
      portalUserId,
      enabled,
    }: {
      portalUserId: string;
      enabled: boolean | null;
    }) => {
      await apiClient.patch(`/portal/users/${portalUserId}/mfa`, { enabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      toast({ variant: 'success', title: 'MFA setting updated' });
    },
    onError: () => toast({ variant: 'error', title: 'Failed to update MFA setting' }),
  });

  const [tempPasswordUser, setTempPasswordUser] = useState<PortalUser | null>(null);
  const [tempPasswordResult, setTempPasswordResult] = useState<string | null>(null);

  const tempPasswordMutation = useMutation({
    mutationFn: async ({
      portalUserId,
      temporaryPassword,
    }: {
      portalUserId: string;
      temporaryPassword?: string;
    }) => {
      const res = await apiClient.post<{ success: boolean; data: { temporaryPassword: string } }>(
        `/portal/users/${portalUserId}/temporary-password`,
        temporaryPassword ? { temporaryPassword } : {},
      );
      return res.data.data.temporaryPassword;
    },
    onSuccess: (password) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-users'] });
      setTempPasswordUser(null);
      setTempPasswordResult(password);
      toast({ variant: 'success', title: 'Temporary password set' });
    },
    onError: () => toast({ variant: 'error', title: 'Failed to set temporary password' }),
  });

  const portalColumns: ColumnDef<PortalUser>[] = useMemo(
    () => [
      {
        id: 'contactName',
        header: 'Client Name',
        sortable: false,
        cell: (row) =>
          can(PERMISSIONS.CONTACTS_READ) ? (
            <button
              type="button"
              onClick={() => navigate(`/contacts/${row.contactId}`)}
              className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
            >
              {row.contactName}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {row.contactName}
            </span>
          ),
      },
      {
        id: 'email',
        header: 'Email',
        sortable: false,
        cell: (row) => (
          <span className="text-sm text-gray-600 dark:text-gray-400">{row.email}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: false,
        cell: (row) => {
          const variant =
            row.status === 'active' ? 'success' : row.status === 'invited' ? 'info' : 'danger';
          return (
            <Badge variant={variant} size="sm" dot>
              {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
            </Badge>
          );
        },
      },
      {
        id: 'lastLogin',
        header: 'Last Login',
        sortable: false,
        cell: (row) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleDateString() : 'Never'}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Created',
        sortable: false,
        cell: (row) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {new Date(row.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'access',
        header: 'Access / MFA',
        sortable: false,
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.accessBlocked && (
              <Badge variant="danger" size="sm">
                Blocked
              </Badge>
            )}
            <Badge variant={row.mfaEnabled === false ? 'warning' : 'info'} size="sm">
              MFA: {row.mfaEnabled === null ? 'Default' : row.mfaEnabled ? 'On' : 'Off'}
            </Badge>
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <RowActionsMenu
            actions={[
              { label: 'Edit', onClick: () => setEditingPortalUser(row) },
              { label: 'Set temporary password', onClick: () => setTempPasswordUser(row) },
              {
                label:
                  row.mfaEnabled === null
                    ? 'MFA: disable'
                    : row.mfaEnabled
                      ? 'MFA: reset to default'
                      : 'MFA: enable',
                onClick: () =>
                  mfaMutation.mutate({
                    portalUserId: row.id,
                    // cycle: default -> off -> on -> default
                    enabled: row.mfaEnabled === null ? false : row.mfaEnabled ? null : true,
                  }),
              },
              {
                label: 'Reactivate',
                hidden: row.status !== 'suspended',
                onClick: () => reactivateMutation.mutate(row.id),
              },
              {
                label: 'Resend invite',
                hidden: row.status !== 'invited' && row.status !== 'suspended',
                onClick: () => resendMutation.mutate(row.id),
              },
              {
                label: row.accessBlocked ? 'Grant access' : 'Block access',
                danger: !row.accessBlocked,
                onClick: () =>
                  accessMutation.mutate({ portalUserId: row.id, blocked: !row.accessBlocked }),
              },
              {
                label: 'Suspend',
                hidden: row.status !== 'active',
                danger: true,
                onClick: () => suspendMutation.mutate(row.id),
              },
              {
                label: 'Revoke invite',
                hidden: row.status !== 'invited',
                danger: true,
                onClick: () => revokeMutation.mutate(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [
      suspendMutation,
      reactivateMutation,
      resendMutation,
      revokeMutation,
      accessMutation,
      mfaMutation,
      resendingPortalUserId,
      can,
      navigate,
    ],
  );

  return (
    <>
      <Card>
        <CardHeader
          title="Portal Users (Clients)"
          description="Manage client portal access, activation, and invitations."
        />
        <CardContent>
          <div className="mb-4 w-full sm:w-56">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'invited', label: 'Invited' },
                { value: 'suspended', label: 'Suspended' },
              ]}
              placeholder="All statuses"
            />
          </div>
          <DataTable<PortalUser>
            data={filteredPortalUsers}
            columns={portalColumns}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="No portal users found."
            filterValue={searchFilter}
            onFilterChange={setSearchFilter}
            filterPlaceholder="Search by name or email..."
          />
        </CardContent>
      </Card>

      {/* Edit Portal User Modal */}
      {editingPortalUser && (
        <EditPortalUserModal
          portalUser={editingPortalUser}
          onSave={(data) =>
            updatePortalUserMutation.mutate({
              portalUserId: editingPortalUser.id,
              ...data,
            })
          }
          onClose={() => setEditingPortalUser(null)}
          isSaving={updatePortalUserMutation.isPending}
        />
      )}

      {/* Set Temporary Password Modal */}
      {tempPasswordUser && (
        <TemporaryPasswordModal
          portalUser={tempPasswordUser}
          onSave={(temporaryPassword) =>
            tempPasswordMutation.mutate({
              portalUserId: tempPasswordUser.id,
              temporaryPassword,
            })
          }
          onClose={() => setTempPasswordUser(null)}
          isSaving={tempPasswordMutation.isPending}
        />
      )}

      {/* Temporary Password Result Modal */}
      {tempPasswordResult && (
        <Modal
          open
          onClose={() => setTempPasswordResult(null)}
          title="Temporary Password Set"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Share this password securely with the client. They will be required to change it on
              their next login.
            </p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center font-mono text-base dark:border-border-dark dark:bg-gray-800">
              {tempPasswordResult}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setTempPasswordResult(null)}>Done</Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Temporary Password Modal ─────────────────────────────────────────────────

interface TemporaryPasswordModalProps {
  portalUser: PortalUser;
  onSave: (temporaryPassword?: string) => void;
  onClose: () => void;
  isSaving: boolean;
}

function TemporaryPasswordModal({
  portalUser,
  onSave,
  onClose,
  isSaving,
}: TemporaryPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [autoGenerate, setAutoGenerate] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(autoGenerate ? undefined : password.trim());
  };

  return (
    <Modal open onClose={onClose} title={`Temporary Password: ${portalUser.contactName}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Assign a temporary password so the client can sign in immediately. They will be forced to
          set a new password on their next login.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={autoGenerate}
            onChange={(e) => setAutoGenerate(e.target.checked)}
          />
          Auto-generate a secure password
        </label>
        {!autoGenerate && (
          <Input
            label="Temporary Password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required={!autoGenerate}
            minLength={8}
            placeholder="At least 8 characters"
          />
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving}>
            Set Temporary Password
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Edit Portal User Modal ───────────────────────────────────────────────────

interface EditPortalUserModalProps {
  portalUser: PortalUser;
  onSave: (data: { email?: string; feature_config?: Record<string, boolean> }) => void;
  onClose: () => void;
  isSaving: boolean;
}

const FEATURE_LABELS: Record<string, string> = {
  jobs: 'Jobs & Task Tracking',
  documents: 'Documents & File Uploads',
  invoices: 'Invoices & Payments',
  messages: 'Messages',
  notifications: 'Notification Preferences',
};

function EditPortalUserModal({ portalUser, onSave, onClose, isSaving }: EditPortalUserModalProps) {
  const [email, setEmail] = useState(portalUser.email);

  const initialFeatures: Record<string, boolean> = {
    jobs: portalUser.featureConfig.jobs ?? true,
    documents: portalUser.featureConfig.documents ?? true,
    invoices: portalUser.featureConfig.invoices ?? true,
    messages: portalUser.featureConfig.messages ?? true,
    notifications: portalUser.featureConfig.notifications ?? true,
  };

  const [features, setFeatures] = useState<Record<string, boolean>>(initialFeatures);

  const handleToggle = (key: string) => {
    setFeatures((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only include feature_config if any toggle was actually changed
    const featuresChanged = Object.keys(initialFeatures).some(
      (key) => features[key] !== initialFeatures[key],
    );

    onSave({
      email:
        email.trim().toLowerCase() !== portalUser.email ? email.trim().toLowerCase() : undefined,
      feature_config: featuresChanged ? features : undefined,
    });
  };

  return (
    <Modal open onClose={onClose} title={`Edit Portal User: ${portalUser.contactName}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email Section */}
        <Input
          label="Portal Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="client@example.com"
          hint="This is the email the client uses to log in to the portal"
        />

        {/* Portal Configuration Section */}
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Portal Configuration
          </h4>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Control which features are visible to this client on the portal. Disabled features will
            be hidden from navigation and inaccessible.
          </p>
          <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-border-dark">
            {Object.entries(FEATURE_LABELS).map(([key, label]) => (
              <label
                key={key}
                className="flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-surface-dark-tertiary"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                <input
                  type="checkbox"
                  checked={features[key] ?? true}
                  onChange={() => handleToggle(key)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
              </label>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Client: {portalUser.contactName} &middot; Status: {portalUser.status}
        </p>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!email.trim()}>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}
